import React, { Component } from 'react';
import classNames from 'classnames';
import buttonBoilerplate, {
    ButtonBoilerplate,
    ButtonHandlers,
    PressEvent,
    isTouchEvent,
} from './buttonBoilerplate';
import buttonStyle from './button.module.scss';

export interface ButtonProps extends ButtonHandlers<HTMLButtonElement> {
  className?: string;
  style?: {};
    /** 종류 */
  kind?: 'default' | 'primary' | 'solid';
  disabled?: boolean;
  children?: React.ReactNode;
}

interface ButtonState {
  ripples: (RippleProps & { key: number })[];
  lastRippleKey: number;
}

export class Button extends Component<ButtonProps, ButtonState> {
  private static defaultProps: Pick<ButtonProps, 'kind' | 'className'> = {
    kind: 'default',
    className: '',
  };

  private dom?: HTMLElement;
  private boilerplate!: ButtonBoilerplate<HTMLButtonElement>;

  public constructor(props: ButtonProps) {
    super(props);
    this.updateBoilerplate(props);
    this.state = {
      ripples: [],
      lastRippleKey: 0,
    };
  }

  private addRipple(ripple: RippleProps): void {
    const { ripples, lastRippleKey } = this.state;
    const key = lastRippleKey + 1;
    ripple.onTransitionEnd = (): void =>
            this.setState({
                // transitionend 시점의 this.state.ripples는 윗줄의 ripples 객체와 다른 값입니다.
              ripples: this.state.ripples.filter((ripple): boolean => ripple.key !== key),
            });
    this.setState({
      ripples: ripples.concat({ ...ripple, key }),
      lastRippleKey: key,
    });
  }

  public componentWillReceiveProps(nextProps: ButtonProps): void {
    const { props } = this;
    if (
            nextProps.onPress !== props.onPress ||
            nextProps.onRelease !== props.onRelease ||
            nextProps.onClick !== props.onClick
        ) {
      this.updateBoilerplate(nextProps);
    }
  }

  public updateBoilerplate(props: ButtonProps): void {
    if (this.boilerplate) {
      this.boilerplate.tearDown();
    }
    this.boilerplate = buttonBoilerplate({
      ...props,
      onPress: (e): void => this.onPress(e),
    });
  }

  public componentWillUnmount(): void {
    this.boilerplate.tearDown();
  }

  private onPress(e: PressEvent<HTMLButtonElement>): void {
    const props: ButtonProps = this.props;
    props.onPress && props.onPress(e);
    if (this.dom) {
      const { top, left, width, height } = this.dom.getBoundingClientRect();
      const beginSize = Math.min(width, height) * 0.5;
      const endSize = Math.max(width, height) * Math.PI;
      let pageX = 0,
        pageY = 0;
      if (isTouchEvent(e)) {
        ({ pageX, pageY } = e.changedTouches[0]);
      } else {
        ({ pageX, pageY } = e);
      }
      this.addRipple({
        top: pageY - top,
        left: pageX - left,
        beginSize,
        endSize,
      });
    }
  }

  public render(): React.ReactElement {
    const { props } = this;
    const className = classNames(this.props.className, {
      [buttonStyle.button]: true,
      [buttonStyle.disabled]: !!props.disabled,
      [buttonStyle.default]: props.kind === 'default',
      [buttonStyle.primary]: props.kind === 'primary',
      [buttonStyle.solid]: props.kind === 'solid',
    });
    return (
            <button
                ref={(dom): void => {
                  this.dom = dom || undefined;
                }}
                className={className}
                style={props.style}
                disabled={props.disabled}
                {...(props.disabled ? {} : this.boilerplate.props)}
            >
                {props.children}
                {this.state.ripples.map(
                    (ripple, index): React.ReactElement => (
                        <Ripple {...ripple} key={index}/>
                    ),
                )}
            </button>
    );
  }
}

interface RippleProps {
  top: number;
  left: number;
  beginSize: number;
  endSize: number;
  onTransitionEnd?(): void;
}

interface RippleState {
  transitioning: boolean;
}

export class Ripple extends React.Component<RippleProps, RippleState> {
  private dom?: HTMLElement;

  public constructor(props: RippleProps) {
    super(props);
    this.state = {
      transitioning: false,
    };
  }

  public componentDidMount(): void {
    if (this.dom) {
      void this.dom.offsetHeight; // transition hack
    }
    this.setState({
      transitioning: true,
    });
  }

  public render(): React.ReactElement {
    const { top, left, beginSize, endSize, onTransitionEnd } = this.props;
    const { transitioning } = this.state;
    const size = transitioning ? endSize : beginSize;
    return (
            <span
                ref={(dom): void => {
                  this.dom = dom || undefined;
                }}
                className={buttonStyle.ripple}
                style={{
                  top,
                  left,
                  width: size,
                  height: size,
                  opacity: transitioning ? 0 : 0.6,
                }}
                onTransitionEnd={onTransitionEnd || ((): void => {})}
            />
    );
  }
}

interface TappableProps extends ButtonHandlers {
  _component?: React.ElementType;
  _ref?(dom: HTMLElement): any;
  [extraProps: string]: any;
}

export class Tappable extends React.Component<TappableProps, {}> {
  static defaultProps = {
    _component: 'div',
  };
  private boilerplate!: ButtonBoilerplate<HTMLButtonElement>;
  constructor(props: TappableProps) {
    super(props);
    this.updateBoilerplate(props);
  }
  componentWillReceiveProps(nextProps: TappableProps) {
    const { props } = this;
    if (
            nextProps.onPress !== props.onPress ||
            nextProps.onRelease !== props.onRelease ||
            nextProps.onClick !== props.onClick
        ) {
      this.updateBoilerplate(nextProps);
    }
  }
  updateBoilerplate(props: TappableProps) {
    this.boilerplate = buttonBoilerplate(props, this.boilerplate);
  }
  componentWillUnmount() {
    this.boilerplate.tearDown();
  }
  render() {
        /* tslint:disable:trailing-comma */
    const {
            _component,
            _ref,
            children,
            onPress,
            onRelease,
            onGlobalRelease,
            onClick,
            ...props
        } = this.props;
    const Component = _component!;
    return (
            <Component ref={_ref} {...props} {...this.boilerplate.props}>
                {children}
            </Component>
    );
  }
}
